import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  FiUsers,
  FiCalendar,
  FiBookOpen,
  FiBarChart2,
  FiFileText,
  FiAlertTriangle,
  FiBell,
  FiLogOut,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationDropdown from './NotificationDropdown';

const teamSections = [
  {
    title: 'Life Support Courses',
    icon: FiBookOpen,
    items: [
      { label: 'Staff Table', path: '/team/staff', icon: FiUsers },
      { label: 'Courses', path: '/team/courses', icon: FiBookOpen },
      { label: 'Calendar', path: '/team/courses/calendar', icon: FiCalendar },
      { label: 'Expiry Tracker', path: '/team/expiry-tracker', icon: FiAlertTriangle },
    ],
  },
  {
    title: 'Grand Rounds',
    icon: FiFileText,
    items: [
      { label: 'Activities', path: '/team/grand-rounds', icon: FiFileText },
      { label: 'Calendar', path: '/team/grand-rounds/calendar', icon: FiCalendar },
    ],
  },
  {
    title: 'Training',
    icon: FiUsers,
    items: [
      { label: 'Trainees', path: '/team/trainees', icon: FiUsers },
    ],
  },
  {
    title: 'On Job Training',
    icon: FiBookOpen,
    items: [
      { label: 'OJT Records', path: '/team/ojt', icon: FiBookOpen },
    ],
  },
];

const dioSections = [
  {
    title: null,
    items: [
      { label: 'Dashboard', path: '/dio/dashboard', icon: FiBarChart2 },
      { label: 'Reports', path: '/dio/reports', icon: FiFileText },
      { label: 'Staff Overview', path: '/dio/staff', icon: FiUsers },
      { label: 'Process Tracker', path: '/dio/trainees', icon: FiUsers },
    ],
  },
];

export default function Layout({ role }) {
  const { user, logout } = useAuth();
  const { unreadCount, fetchNotifications } = useNotifications();
  const [openSections, setOpenSections] = useState({});
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();

  const sections = role === 'dio' ? dioSections : teamSections;

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Auto-open the section containing the current path
  useEffect(() => {
    sections.forEach((section, idx) => {
      if (section.items.some((item) => location.pathname.startsWith(item.path))) {
        setOpenSections((prev) => ({ ...prev, [idx]: true }));
      }
    });
  }, [location.pathname, sections]);

  const toggleSection = (idx) => {
    setOpenSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const pageTitle = (() => {
    for (const section of sections) {
      for (const item of section.items) {
        if (location.pathname === item.path) return item.label;
      }
    }
    return role === 'dio' ? 'Director Portal' : 'Team Portal';
  })();

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>NEOM Hospital</span>
          <span style={styles.logoSub}>ATA Hub</span>
        </div>

        <nav style={styles.nav}>
          {sections.map((section, sIdx) => (
            <div key={sIdx} style={styles.sectionWrap}>
              {section.title && (
                <button
                  style={styles.sectionHeader}
                  onClick={() => toggleSection(sIdx)}
                >
                  <div style={styles.sectionLeft}>
                    {section.icon && <section.icon size={16} />}
                    <span>{section.title}</span>
                  </div>
                  {openSections[sIdx] ? (
                    <FiChevronDown size={14} />
                  ) : (
                    <FiChevronRight size={14} />
                  )}
                </button>
              )}

              {(!section.title || openSections[sIdx]) && (
                <div style={section.title ? styles.subItems : undefined}>
                  {section.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      style={({ isActive }) => ({
                        ...styles.navItem,
                        ...(section.title ? styles.navItemIndented : {}),
                        ...(isActive ? styles.navItemActive : {}),
                      })}
                    >
                      <item.icon size={16} />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.sidebarUser}>
            <div style={styles.avatar}>
              {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{user?.name || user?.username}</div>
              <div style={styles.userRole}>{user?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div style={styles.main}>
        {/* Top bar */}
        <header style={styles.topbar}>
          <h2 style={styles.pageTitle}>{pageTitle}</h2>
          <div style={styles.topRight}>
            <div style={{ position: 'relative' }}>
              <button
                style={styles.bellBtn}
                onClick={() => setNotifOpen(!notifOpen)}
              >
                <FiBell size={20} />
                {unreadCount > 0 && (
                  <span style={styles.badge}>{unreadCount}</span>
                )}
              </button>
              <NotificationDropdown
                isOpen={notifOpen}
                onClose={() => setNotifOpen(false)}
              />
            </div>
            <div style={styles.topUser}>
              <span style={styles.topUserName}>
                {user?.name || user?.username}
              </span>
              <button style={styles.logoutBtn} onClick={logout}>
                <FiLogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main style={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  /* Sidebar */
  sidebar: {
    width: '260px',
    backgroundColor: '#2B362E',
    color: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    overflowY: 'auto',
  },
  logo: {
    padding: '24px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
  },
  logoText: {
    fontSize: '1.1rem',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  logoSub: {
    fontSize: '0.8rem',
    color: '#B2AB9C',
    marginTop: '2px',
  },
  nav: {
    flex: 1,
    padding: '12px 0',
  },
  sectionWrap: {
    marginBottom: '4px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '10px 20px',
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    cursor: 'pointer',
  },
  sectionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  subItems: {
    paddingBottom: '4px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 20px',
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.875rem',
    fontWeight: 400,
    textDecoration: 'none',
    transition: 'all 0.15s',
    borderLeft: '3px solid transparent',
  },
  navItemIndented: {
    paddingLeft: '36px',
  },
  navItemActive: {
    backgroundColor: 'rgba(107, 128, 99, 0.25)',
    color: '#ffffff',
    fontWeight: 500,
    borderLeftColor: '#6B8063',
  },
  sidebarFooter: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  sidebarUser: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: '#6B8063',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  userInfo: {
    overflow: 'hidden',
  },
  userName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userRole: {
    fontSize: '0.7rem',
    color: '#B2AB9C',
    textTransform: 'capitalize',
  },
  /* Main area */
  main: {
    flex: 1,
    marginLeft: '260px',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  topbar: {
    height: '60px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e8e3da',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  pageTitle: {
    fontSize: '1.15rem',
    fontWeight: 600,
    color: '#2B362E',
    margin: 0,
  },
  topRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  bellBtn: {
    position: 'relative',
    background: 'none',
    border: 'none',
    color: '#2B362E',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  badge: {
    position: 'absolute',
    top: '0',
    right: '0',
    backgroundColor: '#C0392B',
    color: '#ffffff',
    fontSize: '0.65rem',
    fontWeight: 700,
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  topUser: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  topUserName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#2B362E',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: '1px solid #e0dcd4',
    borderRadius: '8px',
    padding: '6px 14px',
    fontSize: '0.8rem',
    color: '#6b6b6b',
    cursor: 'pointer',
  },
  content: {
    flex: 1,
    padding: '28px',
    overflowY: 'auto',
  },
};
